import { AnimatePresence, motion } from "framer-motion";
import { History, PanelLeft, Sparkles } from "lucide-react";
import { useEffect, useRef, useState, type ReactNode } from "react";

import type {
  EstimateRenderState,
  EstimateRequest,
  RenderPayload,
} from "../../lib/agent-types";
import { fetchCashToClosePreview } from "../../lib/cash-to-close";
import type { DashboardSnapshot } from "../../lib/dashboard";
import { fetchDownPaymentComparisonPreview } from "../../lib/down-payment-comparison";
import { fetchEstimatePreview, isAbortError } from "../../lib/estimates";
import { dollarsToCents } from "../../lib/financials";
import { fetchRentVsBuyPreview } from "../../lib/rent-vs-buy";
import { cn } from "../../lib/utils";
import { useAdvisorStore, type AdvisorDockItem } from "../../stores/advisor";
import { Button } from "../ui/button";
import { Dialog, DialogContent } from "../ui/dialog";
import { RenderPayloadView } from "./render-registry";

const DOCK_EMPTY_PROMPTS = [
  "Estimate my monthly payment",
  "Compare down payment options",
  "How much home can I afford?",
] as const;

export function AdvisorWidgetDock({
  contextRail,
  currentSnapshotId,
  examplePrompts = DOCK_EMPTY_PROMPTS,
  isStreaming = false,
  items = [],
  latestProfileRecencyToken,
  latestProfileSnapshotId,
  onCollapseWorkspace,
  onExamplePrompt,
  snapshot,
  token,
}: {
  contextRail?: ReactNode;
  // currentSnapshotId is the user's active profile snapshot ID, sourced
  // from the dashboard bootstrap (/api/users/me). Items whose source
  // snapshot has been superseded render a subtle "Based on previous
  // inputs" badge above the card so the user knows the numbers are
  // anchored to an older view of their finances.
  currentSnapshotId?: string | null;
  examplePrompts?: readonly string[];
  isStreaming?: boolean;
  items?: AdvisorDockItem[];
  latestProfileRecencyToken?: string | null;
  // latestProfileSnapshotId is the most recent snapshot ID announced by
  // the chat WebSocket via the `info: profile_snapshot_updated` frame
  // (orchestrator emits it after `update_profile_field` runs). It can
  // arrive before /api/users/me bootstrap re-fetches `currentSnapshotId`,
  // so we treat both as "known current": a widget is stale only when its
  // snapshot matches NEITHER. Without this fast-path, "agent updated my
  // income" did not badge prior widgets as stale until the bootstrap
  // round-trip completed (staging-incident-2, conversation 29b22b04).
  latestProfileSnapshotId?: string | null;
  /** Collapses the workspace pane to a narrow icon rail. */
  onCollapseWorkspace?: () => void;
  onExamplePrompt?: (text: string) => void;
  snapshot?: DashboardSnapshot;
  token?: string;
}) {
  const updateDockItemPayload = useAdvisorStore(
    (state) => state.updateDockItemPayload,
  );
  const [isWorkspaceOpen, setIsWorkspaceOpen] = useState(false);
  const [recomputedItems, setRecomputedItems] = useState<
    Record<
      string,
      {
        error?: boolean;
        isLoading?: boolean;
        payload?: RenderPayload;
        profileSnapshotId?: string;
        profileRecencyToken?: string;
      }
    >
  >({});
  const inFlightKeys = useRef(new Set<string>());
  const recomputedItemsRef = useRef(recomputedItems);

  useEffect(() => {
    recomputedItemsRef.current = recomputedItems;
  }, [recomputedItems]);

  useEffect(() => {
    if (!currentSnapshotId || !snapshot) return;

    const controller = new AbortController();

    // Two snapshot sources, two purposes:
    //   - latestProfileSnapshotId arrives over the chat WS in the same
    //     turn the agent ran update_profile_field; treat it as the
    //     authoritative current ID for visual staleness so the badge
    //     appears immediately (staging-incident-2 fast-path).
    //   - currentSnapshotId is the bootstrap value and tracks the
    //     DashboardSnapshot data this dock recomputes against. Only when
    //     the bootstrap has caught up do we refetch, otherwise the
    //     refetch would run with pre-update profile fields and produce
    //     numbers that don't match the user's just-saved inputs.
    const effectiveCurrent = latestProfileSnapshotId ?? currentSnapshotId;
    const bootstrapCaughtUp = currentSnapshotId === effectiveCurrent;
    for (const item of items) {
      const recomputed = recomputedItemsRef.current[item.id];
      const profileRecencyToken =
        recomputed?.profileRecencyToken ?? item.profileRecencyToken;
      const isStale =
        Boolean(profileRecencyToken) &&
        Boolean(latestProfileRecencyToken) &&
        profileRecencyToken !== latestProfileRecencyToken;
      const key = `${item.id}:${effectiveCurrent}`;
      if (!isStale) {
        continue;
      }
      // Always reflect "we know this is stale" in the UI immediately,
      // even if we can't recompute yet because the bootstrap is behind.
      if (!bootstrapCaughtUp) {
        if (
          !recomputed?.isLoading &&
          recomputed?.profileSnapshotId !== effectiveCurrent
        ) {
          setRecomputedItems((state) => ({
            ...state,
            [item.id]: {
              ...state[item.id],
              isLoading: true,
            },
          }));
        }
        continue;
      }
      if (inFlightKeys.current.has(key)) {
        continue;
      }

      inFlightKeys.current.add(key);
      setRecomputedItems((state) => ({
        ...state,
        [item.id]: {
          ...state[item.id],
          isLoading: true,
        },
      }));

      void recomputeRenderPayload(
        recomputed?.payload ?? item.payload,
        snapshot,
        token,
        controller.signal,
      )
        .then((payload) => {
          updateDockItemPayload({
            id: item.id,
            payload,
            profileSnapshotId: currentSnapshotId,
            profileRecencyToken: latestProfileRecencyToken ?? undefined,
          });
          setRecomputedItems((state) => ({
            ...state,
            [item.id]: {
              isLoading: false,
              payload,
              profileSnapshotId: currentSnapshotId,
              profileRecencyToken: latestProfileRecencyToken ?? undefined,
            },
          }));
        })
        .catch((err: unknown) => {
          if (isAbortError(err)) return;
          setRecomputedItems((state) => ({
            ...state,
            [item.id]: {
              ...state[item.id],
              error: true,
              isLoading: false,
            },
          }));
        })
        .finally(() => {
          inFlightKeys.current.delete(key);
        });
    }

    return () => {
      controller.abort();
    };
  }, [
    currentSnapshotId,
    items,
    latestProfileRecencyToken,
    latestProfileSnapshotId,
    snapshot,
    token,
    updateDockItemPayload,
  ]);

  // Skeleton condition: a turn is in flight but no widget has arrived yet.
  // Once the first render frame lands, items.length > 0 and the skeleton
  // is replaced by the live card. We keep the skeleton out when items are
  // already present — re-renders during streaming should look quiet.
  const showSkeleton = isStreaming && items.length === 0;
  const hasContent = items.length > 0 || showSkeleton;
  // The latest assistant turn's widgets all share this id; everything
  // matching it stays full-opacity, older groups dim.
  const currentSourceId = items[0]?.sourceMessageId;

  return (
    <div
      className="flex h-full min-h-0 flex-col gap-4"
      data-slot="advisor-widget-dock"
    >
      {/*
        SILL-247 padding cleanup: replaced `pb-3` with symmetric `py-2` so
        the workspace divider line sits at the same y-coordinate as the
        top of the chat-pane splitter rail (in advisor-chat-surface). The
        two dividers now meet at a clean corner instead of leaving a 12px
        gap above the horizontal line.
      */}
      <div
        className="w-full shrink-0 border-b border-border/60"
        data-slot="advisor-workspace-header"
      >
        <div className="flex min-h-7 items-center justify-between gap-3 px-2 py-3">
          <div className="flex min-w-0 items-center gap-1">
            <button
              aria-label="Collapse workspace"
              aria-expanded="true"
              className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-[background-color,color,transform,opacity] hover:bg-muted/60 hover:text-foreground active:scale-[0.96] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
              data-slot="advisor-workspace-collapse"
              onClick={onCollapseWorkspace}
              type="button"
            >
              <PanelLeft aria-hidden className="h-4 w-4 scale-x-[-1]" />
            </button>
            <p
              className="whitespace-nowrap text-sm font-semibold text-foreground"
              data-slot="advisor-workspace-title"
            >
              Workspace
            </p>
          </div>
          {contextRail ? (
            <Button
              data-slot="advisor-workspace-button"
              onClick={() => setIsWorkspaceOpen(true)}
              size="sm"
              type="button"
              variant="ghost"
            >
              View context
            </Button>
          ) : null}
        </div>
      </div>

      {hasContent ? (
        <div
          className="min-h-0 flex-1 space-y-4 overflow-y-auto overscroll-contain px-6 pb-0"
          data-slot="advisor-dock-items"
        >
          <AnimatePresence initial={false}>
            {showSkeleton ? (
              <motion.div
                animate={{ opacity: 1, y: 0 }}
                data-slot="advisor-dock-skeleton"
                exit={{ opacity: 0, y: -8 }}
                initial={{ opacity: 0, y: 12 }}
                key="dock-skeleton"
                transition={{ duration: 0.24, ease: [0.22, 1, 0.36, 1] }}
              >
                <DockSkeleton />
              </motion.div>
            ) : null}
            {items.map((item) => {
              const recomputed = recomputedItems[item.id];
              const payload = recomputed?.payload ?? item.payload;
              const profileRecencyToken =
                recomputed?.profileRecencyToken ?? item.profileRecencyToken;
              const isStale =
                Boolean(profileRecencyToken) &&
                Boolean(latestProfileRecencyToken) &&
                profileRecencyToken !== latestProfileRecencyToken;
              const isCurrent =
                currentSourceId !== undefined &&
                item.sourceMessageId === currentSourceId;
              return (
                <motion.div
                  animate={{
                    opacity: isCurrent ? 1 : 0.55,
                    scale: 1,
                    y: 0,
                  }}
                  className={cn(
                    "transition-opacity",
                    !isCurrent && "hover:opacity-100",
                  )}
                  data-dock-position={isCurrent ? "current" : "history"}
                  data-slot="advisor-dock-item"
                  data-stale={isStale ? "true" : undefined}
                  exit={{ opacity: 0, y: -12 }}
                  initial={{ opacity: 0, scale: 0.985, y: 20 }}
                  key={item.id}
                  layout
                  transition={{
                    delay: isCurrent ? 0 : 0.03,
                    duration: 0.3,
                    ease: [0.22, 1, 0.36, 1],
                    layout: { duration: 0.24 },
                    opacity: { duration: 0.35 },
                  }}
                >
                  {isStale && recomputed?.isLoading ? (
                    <div
                      className="mb-2 inline-flex items-center gap-1.5 rounded-full bg-primary-soft px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-[0.14em] text-primary-dark"
                      data-slot="advisor-dock-refreshing-badge"
                      role="status"
                    >
                      <Sparkles aria-hidden className="h-3 w-3" />
                      Refreshing latest inputs
                    </div>
                  ) : isStale ? (
                    <div
                      className="mb-2 inline-flex items-center gap-1.5 rounded-full bg-warning/15 px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-[0.14em] text-warning-foreground"
                      data-slot="advisor-dock-stale-badge"
                      role="status"
                    >
                      <History aria-hidden className="h-3 w-3" />
                      Based on previous inputs
                    </div>
                  ) : null}
                  <RenderPayloadView payload={payload} />
                </motion.div>
              );
            })}
          </AnimatePresence>
        </div>
      ) : (
        <DockEmptyState examples={examplePrompts} onExample={onExamplePrompt} />
      )}

      {contextRail ? (
        <Dialog open={isWorkspaceOpen} onOpenChange={setIsWorkspaceOpen}>
          <DialogContent
            className="sm:max-w-lg"
            description="Financial inputs and the current advisor summary."
            title="Workspace"
          >
            {contextRail}
          </DialogContent>
        </Dialog>
      ) : null}
    </div>
  );
}

async function recomputeRenderPayload(
  payload: RenderPayload,
  snapshot: DashboardSnapshot,
  token: string | undefined,
  signal: AbortSignal,
): Promise<RenderPayload> {
  switch (payload.type) {
    case "monthly_payment":
    case "dti":
    case "loan_product_preview":
      return {
        ...payload,
        estimate: await recomputeEstimate(payload.estimate, snapshot, signal),
      };
    case "cash_to_close": {
      const estimate = await recomputeEstimate(
        payload.estimate,
        snapshot,
        signal,
      );
      if (!token) return { ...payload, estimate };
      const { breakdown } = await fetchCashToClosePreview(
        { estimate: estimate.request },
        { signal, token },
      );
      return { ...payload, breakdown, estimate };
    }
    case "down_payment_comparison": {
      const estimate = await recomputeEstimate(
        payload.estimate,
        snapshot,
        signal,
      );
      if (!token) return { ...payload, estimate };
      const downPaymentPercents = payload.comparison?.columns.map(
        (column) => column.downPaymentPercent,
      );
      const { comparison } = await fetchDownPaymentComparisonPreview(
        {
          estimate: estimate.request,
          down_payment_percents: downPaymentPercents,
        },
        { signal, token },
      );
      return { ...payload, comparison, estimate };
    }
    case "rent_vs_buy": {
      const buy = await recomputeEstimate(payload.buy, snapshot, signal);
      if (!token) return { ...payload, buy };
      const { comparison } = await fetchRentVsBuyPreview(
        {
          estimate: buy.request,
          monthly_rent: payload.rent.monthlyRent,
          years_in_home: payload.rent.yearsInHome,
          assumptions: payload.comparison?.assumptions,
        },
        { signal, token },
      );
      return { ...payload, buy, comparison };
    }
    case "affordability_scenarios": {
      const scenarios = await Promise.all(
        payload.scenarios.map(async (scenario) => ({
          ...scenario,
          estimate: await recomputeEstimate(
            scenario.estimate,
            snapshot,
            signal,
          ),
        })),
      );
      return { ...payload, scenarios };
    }
    default:
      return payload;
  }
}

async function recomputeEstimate(
  estimate: EstimateRenderState,
  snapshot: DashboardSnapshot,
  signal: AbortSignal,
): Promise<EstimateRenderState> {
  const request = updateEstimateRequestForSnapshot(estimate.request, snapshot);
  const response = await fetchEstimatePreview(request, { signal });
  return {
    ...estimate,
    request,
    results: response.results,
  };
}

function updateEstimateRequestForSnapshot(
  request: EstimateRequest,
  snapshot: DashboardSnapshot,
): EstimateRequest {
  const targetPrice =
    request.property.target_price ?? dollarsToCents(snapshot.targetPrice);
  const grossAnnualIncome = dollarsToCents(snapshot.householdIncome);
  const monthlyIncome = dollarsToCents(snapshot.householdIncome / 12);
  const liquidAssets = dollarsToCents(snapshot.savings);
  const monthlyDebts = dollarsToCents(snapshot.monthlyDebts);
  const financing = { ...request.financing };

  if (
    (financing.down_payment_strategy === "target" ||
      financing.down_payment_strategy === "fixed_amount") &&
    targetPrice > 0
  ) {
    const downPaymentAmount = Math.min(liquidAssets, targetPrice);
    financing.down_payment_amount = downPaymentAmount;
    financing.down_payment_percent = downPaymentAmount / targetPrice;
    financing.down_payment_strategy = "fixed_amount";
  }

  const product = normalizeLoanProduct(financing.product);
  financing.product = product;

  return {
    ...request,
    as_of: new Date().toISOString(),
    borrower: {
      ...request.borrower,
      credit_bucket: snapshot.creditBucket ?? undefined,
      employment_months: snapshot.employmentMonths ?? undefined,
      gross_annual_income: grossAnnualIncome,
      has_bonus_income:
        snapshot.hasBonusIncome ?? request.borrower.has_bonus_income,
      has_commission_income:
        snapshot.hasCommissionIncome ?? request.borrower.has_commission_income,
      has_second_job: snapshot.hasSecondJob ?? request.borrower.has_second_job,
      is_self_employed:
        snapshot.isSelfEmployed ?? request.borrower.is_self_employed,
      liquid_assets: liquidAssets,
      monthly_debts: monthlyDebts,
      qualifying_monthly_income: monthlyIncome,
    },
    financing,
    property: {
      ...request.property,
      target_price: targetPrice,
    },
    requested_products: normalizeRequestedProducts(
      request.requested_products,
      product,
    ),
  };
}

function normalizeRequestedProducts(
  products: EstimateRequest["requested_products"],
  fallback: NonNullable<EstimateRequest["financing"]["product"]>,
) {
  const normalized = (products ?? [fallback]).map(normalizeLoanProduct);
  return Array.from(new Set(normalized));
}

function normalizeLoanProduct(
  product: EstimateRequest["financing"]["product"],
): NonNullable<EstimateRequest["financing"]["product"]> {
  switch (product) {
    case "conventional_30":
      return "conventional";
    case "fha_30":
      return "fha";
    case "va_30":
      return "va";
    case "usda_30":
      return "usda";
    default:
      return product ?? "conventional";
  }
}

function DockSkeleton() {
  return (
    <div
      aria-live="polite"
      className="surface-panel p-4"
      data-slot="advisor-dock-skeleton-card"
      role="status"
    >
      <div className="flex items-center gap-2 text-xs font-semibold text-primary-dark">
        <span
          aria-hidden
          className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-primary-soft"
        >
          <Sparkles className="h-3 w-3" />
        </span>
        Running calculation…
      </div>
      <div className="mt-4 space-y-2.5">
        <SkeletonBar className="h-3 w-1/3" />
        <SkeletonBar className="h-9 w-2/3" />
        <SkeletonBar className="h-3 w-5/6" />
      </div>
      <div className="mt-4 grid grid-cols-2 gap-2">
        <SkeletonBar className="h-12" />
        <SkeletonBar className="h-12" />
      </div>
      <span className="sr-only">Sill is generating a result.</span>
    </div>
  );
}

function SkeletonBar({ className }: { className?: string }) {
  return (
    <div
      aria-hidden
      className={cn(
        "animate-pulse rounded-md bg-muted-foreground/15",
        className,
      )}
    />
  );
}

function DockEmptyState({
  examples,
  onExample,
}: {
  examples: readonly string[];
  onExample?: (text: string) => void;
}) {
  return (
    <div
      className="flex min-h-0 flex-1 flex-col items-stretch justify-center gap-4 px-1"
      data-slot="advisor-dock-empty"
    >
      <div className="space-y-1.5 px-1 text-left">
        <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-muted-foreground">
          Try asking
        </p>
        <p className="text-sm leading-5 text-muted-foreground">
          Run one of these to see a live result land here.
        </p>
      </div>
      <div className="space-y-2">
        {examples.map((prompt) => (
          <button
            className="group flex w-full items-center justify-between gap-3 rounded-2xl border border-transparent bg-card px-4 py-3 text-left text-sm text-foreground transition hover:border-primary/40 hover:bg-primary-soft/40 focus-visible:border-primary/50 focus-visible:bg-primary-soft/50 focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-60"
            data-slot="advisor-dock-empty-prompt"
            disabled={!onExample}
            key={prompt}
            onClick={() => onExample?.(prompt)}
            type="button"
          >
            <span className="leading-5">{prompt}</span>
            <span
              aria-hidden
              className="text-muted-foreground transition-colors group-hover:text-primary"
            >
              →
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}
